import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import debounce from 'lodash/debounce';
import SearchBar from '../components/SearchBar';
import NetworkCard from '../components/NetworkCard';
import FAQ from '../components/FAQ';
import networkData from '../data/networkData';
import comparatifData from '../data/comparatifData';
import { FaSearch, FaMousePointer, FaBalanceScale } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Loader from '../components/Loader';

const Home = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simuler un chargement de 2 secondes
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleSearch = useCallback(
    debounce((term) => {
      setSearchTerm(term);
      if (typeof term !== 'string' || term.trim() === '') {
        setSuggestions([]);
        return;
      }

      const lowercasedTerm = term.toLowerCase();

      // Recherche de syntaxes correspondantes
      const results = networkData.flatMap(network => 
        network.syntaxes
          .filter(syntax => 
            syntax.title.toLowerCase().includes(lowercasedTerm) ||
            syntax.content.toLowerCase().includes(lowercasedTerm) ||
            (syntax.keywords && syntax.keywords.some(keyword => 
              keyword.toLowerCase().includes(lowercasedTerm)
            ))
          )
          .map(syntax => ({ ...syntax, networkName: network.name }))
      );

      // Générer des suggestions
      const uniqueSuggestions = [...new Set(results.map(result => ({
        id: result.id,
        title: result.title,
        networkName: result.networkName,
      })))];
      setSuggestions(uniqueSuggestions.slice(0, 5));
    }, 300),
    []
  );

  // Compter le nombre total d'offres
  const totalOffersCount = comparatifData.length;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="container mt-14 mx-auto px-4 py-8">
      {/* Indicateur de comparatif des offres au-dessus de l'accroche */}
      {totalOffersCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-4 text-center"
        >
          <Link 
            to="/offers" 
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-red-600 transition-all duration-300 ease-in-out"
          >
            <FaBalanceScale className="mr-2" />
            Comparatif des offres
          </Link>
        </motion.div>
      )}

      <section className="text-center mt-6 mb-16">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight md:leading-snug lg:leading-normal max-w-3xl mx-auto mb-6"
        >
          Trouvez vos syntaxes en un clin d'œil !
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed"
        >
          Trouvez rapidement les syntaxes "Code USSD" de vos opérateurs mobiles.
        </motion.p>
      </section>

      <SearchBar onSearch={handleSearch} suggestions={suggestions}/>

      <div className="mb-12 mt-10 bg-gray-50 rounded-lg p-8 shadow-sm">
        <h2 className="text-2xl font-semibold text-center mb-8">Comment utiliser l'outil EasySyntaxe ?</h2>
        <div className="flex flex-col md:flex-row justify-center items-center space-y-8 md:space-y-0 md:space-x-12">
          <div className="flex flex-col items-center md:flex-row md:items-start text-center md:text-left">
            <div className="bg-blue-100 p-4 rounded-full mb-4 md:mb-0 md:mr-6 transition-transform hover:scale-110">
              <FaSearch className="text-4xl text-blue-500" />
            </div>
            <p className="text-lg max-w-[200px] md:max-w-none">
              Tapez une syntaxe ou un mot-clé dans la barre de recherche
            </p>
          </div>
          <div className="text-2xl font-bold">OU</div>
          <div className="flex flex-col items-center md:flex-row md:items-start text-center md:text-left">
            <div className="bg-green-100 p-4 rounded-full mb-4 md:mb-0 md:mr-6 transition-transform hover:scale-110">
              <FaMousePointer className="text-4xl text-green-500" />
            </div>
            <div className="text-lg max-w-[200px] md:max-w-none">
              Cliquez sur un réseau pour voir toutes ses <br className="block md:hidden" /> syntaxes
            </div>
          </div>
        </div>
      </div>
            
      <div className="mt-16">
        <h2 className="text-3xl font-semibold text-center mb-8">Choisissez votre réseau</h2>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-8 mb-12"
        >
          {networkData.map((network, index) => (
            <motion.div
              key={network.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <NetworkCard name={network.name} logo={network.logo} />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* FAQ Section */}
      <FAQ/>
    </div>
  );
};

export default Home;